import {
  selAnnotations,
  selAnnotationsPluginState,
  toAnnotationsPluginMessage,
  type AnnotationsPluginMessage,
  type AnnotationBase
} from '@st/document-viewer-kit/plugins/annotations'
import type { Contract, ContractField, Signer } from '@st/sdk'
import { match } from '@st/pattern'
import type { FieldToolState } from '~/st-doc-viewer/annotations'
import {
  viewerFieldAnnotation,
  type ContractFieldAnnotation
} from '~/st-doc-viewer/viewer-annotations'

export type ContractViewer = Parameters<typeof selAnnotationsPluginState>[0]

/** Adapts persisted fields to keyed viewer annotations; field IDs survive editing and saving. */
export function contractDraftFieldAnnotations(contract: Contract, mode: 'editing' | 'viewing') {
  return Object.fromEntries(
    contract.fields.map((field) => [
      field.id,
      viewerFieldAnnotation({
        id: field.id,
        pageNumber: field.page,
        bounds: field.rect,
        field,
        mode,
        signerRole: contract.signers.find((signer) => signer.id == field.signerId)!.role
      }) satisfies ContractFieldAnnotation
    ])
  )
}

/**
 * Materializes the signing overlay. Existing field order stays stable and new
 * fields append in document order; the editor owns dirty comparison.
 */
export function contractDraftFields(contract: Contract, viewer: ContractViewer): ContractField[] {
  const fieldsById: Record<string, ContractField> = Object.fromEntries(
    Object.values(selAnnotations(viewer, isContractFieldAnnotation)).map((annotation) => [
      annotation.id,
      {
        ...annotation.field,
        id: annotation.id,
        page: annotation.pageNumber,
        rect: annotation.bounds
      }
    ])
  )
  const existingFields = contract.fields.flatMap(({ id }) => {
    const field = fieldsById[id]
    if (!field) return []
    delete fieldsById[id]
    return [field]
  })
  const newFields = Object.values(fieldsById).sort(
    (left, right) =>
      left.page - right.page ||
      left.rect.y - right.rect.y ||
      left.rect.x - right.rect.x ||
      left.id.localeCompare(right.id)
  )
  return [...existingFields, ...newFields]
}

/** Selects signing-field edits to discard, including deletion tombstones, without touching comment drafts. */
export function contractDraftFieldChangeIds(viewer: ContractViewer) {
  const { annotations, annotationDrafts } = selAnnotationsPluginState(viewer)
  return Object.entries(annotationDrafts)
    .filter(([id, draft]) => {
      const annotation = draft ?? annotations[id]
      // Deleting a newly created draft leaves a tombstone without a base annotation.
      return annotation && isContractFieldAnnotation(annotation)
    })
    .map(([id]) => id)
}

/** Reads the effective overlay, so signer removal includes both saved fields and unsaved placements. */
function signerFieldAnnotationIds(viewer: ContractViewer, signerId: string) {
  return Object.values(selAnnotations(viewer, isContractFieldAnnotation))
    .filter((annotation) => annotation.field.signerId == signerId)
    .map((annotation) => annotation.id)
}

// Data-entry fields share the annotation type but do not belong to the contract signing definition.
function isContractFieldAnnotation(
  annotation: AnnotationBase
): annotation is ContractFieldAnnotation {
  return annotation.type == 'field' && annotation.mode != 'data'
}

/** Delete through the overlay so Save and Discard keep their ordinary meaning, including new boxes. */
export function removeSignerFields(
  viewer: ContractViewer,
  signerId: string
): AnnotationsPluginMessage[] {
  const messages = signerFieldAnnotationIds(viewer, signerId).map((annotationId) =>
    toAnnotationsPluginMessage({ type: 'deleteAnnotation', annotationId })
  )
  const tool = selAnnotationsPluginState(viewer).toolStates.field as FieldToolState | undefined
  if (tool?.placement?.field.signerId == signerId) {
    const cleared: FieldToolState = { ...tool, placement: undefined }
    messages.push(toAnnotationsPluginMessage({ type: 'updateTool', tool: cleared }))
  }
  return messages
}

/** Selection, tool configuration, and activation are one placement transition, never view sequencing. */
export function placeSignerField(
  signer: Signer,
  fieldType: 'signature' | 'date'
): AnnotationsPluginMessage[] {
  const tool: FieldToolState = {
    name: 'field',
    settings: {},
    placement: {
      field: {
        type: fieldType,
        label: match(fieldType)
          .with('signature', () => 'Signature')
          .with('date', () => 'Date')
          .exhaustive(),
        signerId: signer.id,
        metadata: {}
      },
      signerRole: signer.role
    }
  }
  return [
    toAnnotationsPluginMessage({ type: 'deselectAnnotation' }),
    toAnnotationsPluginMessage({ type: 'updateTool', tool }),
    toAnnotationsPluginMessage({ type: 'selectTool', toolName: 'field' })
  ]
}
